import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from '../charts/d3/format';
import type { LoanPaidToDate } from '../../utils/loanAmortization';
import {
  formatLoanDuration,
  formatMonthKeyLabel,
  formatPayoffMonth,
  relativePaymentPeriod,
} from '../../utils/loanAmortization';

type LoanProgressBlockProps = {
  progress: LoanPaidToDate;
  startMonth: string;
  monthsToPayoff: number;
};

export function LoanProgressBlock({
  progress,
  startMonth,
  monthsToPayoff,
}: LoanProgressBlockProps) {
  const currentLabel = progress.currentMonthKey
    ? formatMonthKeyLabel(progress.currentMonthKey)
    : progress.firstPaymentLabel;
  const payoffLabel = formatPayoffMonth(startMonth, monthsToPayoff);

  if (progress.status === 'unstarted') {
    return (
      <View style={styles.wrap}>
        <Text style={styles.kicker}>Not started yet</Text>
        <Text style={styles.title}>Payments start {progress.firstPaymentLabel}</Text>
        <Text style={styles.hint}>
          Nothing paid on this schedule yet. Dates and “paid so far” stay empty until that month.
        </Text>
      </View>
    );
  }

  if (progress.status === 'paid_off') {
    return (
      <View style={styles.wrap}>
        <Text style={styles.kicker}>Paid off</Text>
        <Text style={styles.title}>Finished {currentLabel}</Text>
        <Text style={styles.hint}>
          Principal {formatCurrency(progress.principalPaid)} · interest{' '}
          {formatCurrency(progress.interestPaid)}
          {progress.extraPaid > 0 ? ` · extras ${formatCurrency(progress.extraPaid)}` : ''}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.wrap}>
      <Text style={styles.kicker}>Paid through {currentLabel}</Text>
      <Text style={styles.title}>{formatCurrency(progress.totalPaid)} paid so far</Text>
      <Text style={styles.hint}>
        You’re in {relativePaymentPeriod(progress.currentMonth)} · {currentLabel}
        {payoffLabel ? ` · payoff ${payoffLabel}` : ''}
      </Text>

      <View style={styles.track}>
        <View style={[styles.trackFill, { width: `${Math.max(progress.progressPct, 2)}%` }]} />
      </View>
      <Text style={styles.progressLabel}>
        {progress.progressPct}% of principal · {formatLoanDuration(progress.monthsRemaining)} left
      </Text>

      <View style={styles.kpiRow}>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Principal paid</Text>
          <Text style={styles.kpiValue}>{formatCurrency(progress.principalPaid)}</Text>
        </View>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Interest paid</Text>
          <Text style={[styles.kpiValue, styles.warning]}>
            {formatCurrency(progress.interestPaid)}
          </Text>
        </View>
      </View>
      <View style={styles.kpiRow}>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Extras paid</Text>
          <Text style={[styles.kpiValue, styles.accent]}>
            {formatCurrency(progress.extraPaid)}
          </Text>
        </View>
        <View style={styles.kpi}>
          <Text style={styles.kpiLabel}>Remaining</Text>
          <Text style={styles.kpiValue}>{formatCurrency(progress.remainingBalance)}</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: 'rgba(61, 214, 164, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.22)',
    gap: 4,
  },
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  title: {
    ...typography.headline,
    fontSize: 22,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  track: {
    height: 8,
    borderRadius: radii.pill,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    marginTop: spacing.sm,
  },
  trackFill: {
    height: '100%',
    borderRadius: radii.pill,
    backgroundColor: colors.accent,
  },
  progressLabel: {
    ...typography.caption,
    color: colors.text,
    fontVariant: ['tabular-nums'],
    marginBottom: spacing.xs,
  },
  kpiRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  kpi: {
    flex: 1,
    gap: 2,
  },
  kpiLabel: {
    ...typography.label,
    color: colors.textMuted,
    textTransform: 'uppercase',
  },
  kpiValue: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
    fontVariant: ['tabular-nums'],
  },
  warning: {
    color: colors.warning,
  },
  accent: {
    color: colors.accent,
  },
});
